import {
  Box,
  Checkbox,
  FieldMessage,
  Heading,
  IconNewWindow,
  Inline,
  RadioGroup,
  RadioItem,
  Stack,
  Text,
  Textarea,
} from 'braid-design-system';
import { useFormContext } from 'react-hook-form';
import slugify from 'slugify';

import { TextLink } from 'src/components/Link/Link';

const integrations = [
  {
    name: 'SEEK Job Posting',
    link: 'https://developer.seek.com/use-cases/job-posting',
  },
  {
    name: 'SEEK Optimised Apply',
    link: 'https://developer.seek.com/use-cases/application-export',
  },
  {
    name: 'Apply with SEEK',
    link: 'https://developer.seek.com/use-cases/apply-with-seek',
  },
  {
    name: 'Talent Search Connect',
    link: 'https://developer.seek.com/use-cases/proactive-sourcing',
  },
];

export const IntegrationDetails = () => {
  const {
    register,
    formState: { errors },
    watch,
    setValue,
    trigger,
  } = useFormContext();

  const solutions: string[] = watch('solutions');

  return (
    <Stack space="medium">
      <Box paddingTop="gutter">
        <Heading level="3">SEEK integration details</Heading>
      </Box>
      {watch('seekRsp') === 'no' && (
        <RadioGroup
          label="Are you a customer wanting to build an integration to SEEK?"
          id="integrateToSeek"
          value={watch('integrateToSeek') || ''}
          onChange={({ currentTarget: { value } }) => {
            setValue('integrateToSeek', value);
            register('integrateToSeek').onChange({ target: { value } });
            trigger('integrateToSeek');
          }}
        >
          <RadioItem label="Yes" value="yes" />
          <RadioItem label="No" value="no" />
        </RadioGroup>
      )}
      {watch('integrateToSeek') === 'no' && (
        <Textarea
          id="reasonForIntegration"
          label="Reason for completing this integration request"
          message={errors.reasonForIntegration?.message as string}
          tone={Boolean(errors.reasonForIntegration) ? 'critical' : 'neutral'}
          {...register('reasonForIntegration')}
          value={watch('reasonForIntegration') || ''}
          onChange={(e) => {
            setValue('reasonForIntegration', e.currentTarget.value);
            register('reasonForIntegration').onChange(e);
            trigger('reasonForIntegration');
          }}
        />
      )}
      <RadioGroup
        label="Are you currently integrated with SEEK?"
        id="currentlyIntegrated"
        value={watch('currentlyIntegrated')}
        onChange={({ currentTarget: { value } }) => {
          setValue('currentlyIntegrated', value);
          register('currentlyIntegrated').onChange({ target: { value } });
        }}
      >
        <RadioItem label="Yes" value="yes" />
        <RadioItem label="No" value="no" />
      </RadioGroup>

      <Stack space="medium">
        <Text weight="strong">
          {watch('currentlyIntegrated') === 'yes'
            ? 'What additional SEEK solutions would you like to support?'
            : 'What SEEK solutions would you like to support?'}
        </Text>

        <Text>
          To integrate with SEEK Job Posting, partners must also support SEEK
          Optimised Apply.
        </Text>

        <Stack space="small">
          {integrations.map(({ name, link }) => {
            const checked = solutions.includes(name);
            return (
              <Inline space="xxsmall" alignY="center" key={link}>
                <Checkbox
                  id={`checkbox-${name.toLocaleLowerCase()}`}
                  checked={checked}
                  onChange={(e) => {
                    const newValue = [
                      ...solutions.filter((solution) => solution !== name),
                      ...(e.currentTarget.checked ? [name] : []),
                    ];

                    setValue('solutions', newValue);
                    register('solutions').onChange({
                      target: { value: newValue },
                    });
                    trigger('solutions');
                  }}
                  label={name}
                  value={name}
                  tone={Boolean(errors.solutions) ? 'critical' : 'neutral'}
                />
                <Text>
                  <TextLink
                    href={link}
                    tracking={`${slugify(name, { lower: true })}-link-clicked`}
                    target="_blank"
                  >
                    <IconNewWindow alignY="lowercase" />
                  </TextLink>
                </Text>
              </Inline>
            );
          })}
        </Stack>

        {Boolean(errors.solutions) ? (
          <FieldMessage
            id="solutions"
            message="Select at least one solution"
            tone="critical"
          />
        ) : null}
      </Stack>

      <Textarea
        id="whySeek"
        label="Please describe why you want to integrate with SEEK"
        {...register('whySeek')}
        message={errors.whySeek?.message as string}
        tone={Boolean(errors.whySeek) ? 'critical' : 'neutral'}
        value={watch('whySeek')}
        onChange={(e) => {
          setValue('whySeek', e.currentTarget.value);
          register('whySeek').onChange(e);
          trigger('whySeek');
        }}
      />
    </Stack>
  );
};
